// GPU-side support for compressed cubemap starmaps.
export const BasisFormat = {
    ETC1_RGB: 0,
    ETC2_RGBA: 1,
    BC1_RGB: 2,
    BC3_RGBA: 3,
    BC7_RGBA: 7,
    ASTC_4x4_RGBA: 10,
    RGBA32: 13
} as const;

const GlFormat = {
    COMPRESSED_RGBA_BPTC_UNORM_EXT: 0x8e8c,
    COMPRESSED_RGBA_ASTC_4x4_KHR: 0x93b0,
    COMPRESSED_RGB_S3TC_DXT1_EXT: 0x83f0,
    COMPRESSED_RGBA8_ETC2_EAC: 0x9278,
    COMPRESSED_RGB_ETC1_WEBGL: 0x8d64
} as const;

export interface StarmapTarget {
    name: string;
    basisFormat: number;
    glFormat: number;
    compressed: boolean;
}

const TARGETS: (StarmapTarget & { ext: string })[] = [
    {
        name: 'BC1',
        ext: 'WEBGL_compressed_texture_s3tc',
        basisFormat: BasisFormat.BC1_RGB,
        glFormat: GlFormat.COMPRESSED_RGB_S3TC_DXT1_EXT,
        compressed: true
    },
    {
        name: 'ETC2',
        ext: 'WEBGL_compressed_texture_etc',
        basisFormat: BasisFormat.ETC2_RGBA,
        glFormat: GlFormat.COMPRESSED_RGBA8_ETC2_EAC,
        compressed: true
    },
    {
        name: 'ASTC 4x4',
        ext: 'WEBGL_compressed_texture_astc',
        basisFormat: BasisFormat.ASTC_4x4_RGBA,
        glFormat: GlFormat.COMPRESSED_RGBA_ASTC_4x4_KHR,
        compressed: true
    },
    {
        name: 'BC7',
        ext: 'EXT_texture_compression_bptc',
        basisFormat: BasisFormat.BC7_RGBA,
        glFormat: GlFormat.COMPRESSED_RGBA_BPTC_UNORM_EXT,
        compressed: true
    },
    {
        name: 'ETC1',
        ext: 'WEBGL_compressed_texture_etc1',
        basisFormat: BasisFormat.ETC1_RGB,
        glFormat: GlFormat.COMPRESSED_RGB_ETC1_WEBGL,
        compressed: true
    }
];

// Uncompressed last resort: 4x the VRAM, but works anywhere.
const FALLBACK_TARGET: StarmapTarget = {
    name: 'RGBA32',
    basisFormat: BasisFormat.RGBA32,
    glFormat: 0x1908, // gl.RGBA
    compressed: false
};

export function selectStarmapTarget(gl: WebGL2RenderingContext): StarmapTarget {
    for (const t of TARGETS) {
        if (gl.getExtension(t.ext)) {
            const { name, basisFormat, glFormat, compressed } = t;
            return { name, basisFormat, glFormat, compressed };
        }
    }
    return FALLBACK_TARGET;
}

export interface StarmapMipLevel {
    width: number;
    height: number;
    data: Uint8Array;
}

export interface StarmapCubemap {
    // [face][level], faces in +X -X +Y -Y +Z -Z order.
    faces: StarmapMipLevel[][];
    width: number;
    height: number;
    faceCount: number;
    levelCount: number;
}

// Produces the transcoded cubemap for a given GPU target. Implemented by the
// host app, which owns the transcoder worker.
export type StarmapProvider = (target: StarmapTarget) => Promise<StarmapCubemap>;

const CUBE_FACE_ORDER = [
    0x8515, // TEXTURE_CUBE_MAP_POSITIVE_X
    0x8516, // NEGATIVE_X
    0x8517, // POSITIVE_Y
    0x8518, // NEGATIVE_Y
    0x8519, // POSITIVE_Z
    0x851a // NEGATIVE_Z
] as const;

export interface UploadResult {
    texture: WebGLTexture;
    levelsUploaded: number;
    busyMs: number;
    frames: number;
}

export async function uploadStarCubemap(
    gl: WebGL2RenderingContext,
    cube: StarmapCubemap,
    target: StarmapTarget,
    opts: { frameBudgetMs?: number; isAborted?: () => boolean } = {}
): Promise<UploadResult> {
    const { frameBudgetMs = 8, isAborted } = opts;
    const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    const maxCube = gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE) as number;
    if (cube.width > maxCube) {
        throw new Error(`starmap face is ${cube.width}px but MAX_CUBE_MAP_TEXTURE_SIZE is ${maxCube}`);
    }

    const minSize = target.compressed ? 4 : 1;
    const usable: number[] = [];
    for (let level = 0; level < cube.levelCount; level++) {
        const m = cube.faces[0][level];
        if (m && m.width >= minSize && m.height >= minSize) {
            usable.push(level);
        }
    }
    if (usable.length === 0) {
        throw new Error('starmap cubemap has no usable mip levels');
    }

    const texture = gl.createTexture();
    if (!texture) {
        throw new Error('failed to create starmap cubemap texture');
    }

    gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    while (gl.getError() !== gl.NO_ERROR) {
        // Drain pre-existing errors so the checks below can't inherit them.
    }

    let busyMs = 0;
    let frames = 1;
    let budgetUsed = 0;

    try {
        for (let face = 0; face < cube.faceCount; face++) {
            const faceTarget = CUBE_FACE_ORDER[face];
            for (let i = 0; i < usable.length; i++) {
                if (isAborted?.()) {
                    throw new Error('starmap upload aborted');
                }

                const m = cube.faces[face][usable[i]];
                const t0 = performance.now();

                gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
                if (target.compressed) {
                    gl.compressedTexImage2D(faceTarget, i, target.glFormat, m.width, m.height, 0, m.data);
                } else {
                    gl.texImage2D(faceTarget, i, gl.RGBA, m.width, m.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, m.data);
                }

                const err = gl.getError();
                if (err !== gl.NO_ERROR) {
                    throw new Error(
                        `GL 0x${err.toString(16)} uploading starmap face ${face} mip ${i} ` +
                            `(${m.width}x${m.height}, ${m.data.byteLength}B, ${target.name})`
                    );
                }

                const dt = performance.now() - t0;
                busyMs += dt;
                budgetUsed += dt;

                if (budgetUsed >= frameBudgetMs) {
                    budgetUsed = 0;
                    frames++;
                    await nextFrame();
                }
            }
        }

        gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAX_LEVEL, usable.length - 1);

        const aniso =
            gl.getExtension('EXT_texture_filter_anisotropic') ??
            gl.getExtension('MOZ_EXT_texture_filter_anisotropic') ??
            gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
        if (aniso) {
            const max = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
            gl.texParameterf(gl.TEXTURE_CUBE_MAP, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(max, 16));
        }

        return { texture, levelsUploaded: usable.length, busyMs, frames };
    } catch (e) {
        gl.deleteTexture(texture);
        throw e;
    }
}



